<template>
  <draggable-panel
    :visible="visible"
    :title="title"
    :icon="icon"
    :width="950"
    :tabs="tabs"
    :active-tab="currentActiveTab"
    :initial-position="{ top: 60 }"
    :z-index="zIndex"
    :enable-expand="true"
    :is-expanded="isExpanded"
    :expanded-width="1460"
    @close="handleClose"
    @tab-change="handleTabChange"
    @panel-focus="handlePanelFocus"
    @expand-toggle="handleExpandToggle"
  >
    <!-- 内容区域 -->
    <div class="panel-content" v-loading="loading">
        <!-- 告警详情 -->
        <div v-show="currentActiveTab === 'detail'" class="alert-detail-container">
          <div class="alert-info-card">
            <!-- 第一行：编号、时间、类型、级别、位置 -->
            <div class="alert-info-row">
              <div class="alert-info-item">
                <span class="label">编号：</span>
                <span class="value">{{ currentAlert.id }}</span>
              </div>
              <div class="alert-info-item">
                <span class="label">时间：</span>
                <span class="value">{{ formatTime(currentAlert.alertTime) }}</span>
              </div>
              <div class="alert-info-item">
                <span class="label">类型：</span>
                <span class="value">{{ getRuleTypeText(currentAlert.ruleType) }}</span>
              </div>
              <div class="alert-info-item">
                <span class="label">级别：</span>
                <span class="value">
                  <el-tag :type="getAlertLevelType(currentAlert.alertLevel)" size="mini">{{ getAlertLevelType(currentAlert.alertLevel) }}</el-tag>
                </span>
              </div>
              <div class="alert-info-item" v-if="currentAlert.longitude && currentAlert.latitude">
                <span class="label">位置：</span>
                <span class="value">{{ formatCoordinate(currentAlert.longitude, currentAlert.latitude) }}</span>
              </div>
            </div>
            
            <!-- 第二行：告警消息 -->
            <div class="alert-message">
              {{ currentAlert.alertMessage }}
            </div>
            
            
            <!-- 告警处理区域 -->
            <div v-if="currentAlert.status === 0" class="alert-handle-area">
              <el-divider>告警处理</el-divider>
              <div class="handle-form">
                <div class="handle-row">
                  <div class="handle-options">
                    <el-radio v-model="handleForm.status" :label="1">已处理</el-radio>
                    <el-radio v-model="handleForm.status" :label="2">已忽略</el-radio>
                  </div>
                </div>
                <div class="handle-row">
                  <el-input 
                    v-model="handleForm.handleResult" 
                    type="textarea" 
                    :rows="2"
                    placeholder="请输入处理结果（可选）" 
                    class="handle-input"
                  ></el-input>
                </div>
                <div class="handle-row">
                  <div class="upload-section">
                    <div class="upload-label">附件图片：</div>
                    <image-upload
                      ref="imageUpload"
                      v-model="handleForm.attachments"
                      :max-files="5"
                      :max-size="10"
                      class="handle-upload"
                    />
                  </div>
                </div>
                <div class="handle-row">
                  <el-button type="danger" size="small" @click="confirmDeleteAlert" :loading="submitLoading">删除告警</el-button>
                  <el-button type="primary" size="small" @click="handleAlert" :loading="submitLoading">提交处理</el-button>
                </div>
              </div>
            </div>
            
            <!-- 已处理告警信息 -->
            <div v-else-if="currentAlert.processor || currentAlert.handleTime" class="alert-handled-info">
              <el-divider>处理信息</el-divider>
              <div class="handle-info-row">
                <div class="info-item">
                  <span class="info-label">处理人：</span>
                  <span>{{ formatProcessor(currentAlert.processor) || '无' }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">处理时间：</span>
                  <span>{{ formatTime(currentAlert.handleTime) || '无' }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">处理结果：</span>
                  <span>{{ currentAlert.handleResult || '无' }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">状态：</span>
                  <span>
                    <el-tag :type="getStatusType(currentAlert.status)" size="mini">{{ getStatusText(currentAlert.status) }}</el-tag>
                  </span>
                </div>
              </div>
              
              <!-- 处理图片显示区域 -->
              <div v-if="currentAlert.handleImageUrls && currentAlert.handleImageUrls.length > 0" class="handle-images-section">
                <el-divider>处理图片</el-divider>
                <div class="handle-images-container">
                  <div 
                    v-for="(imageUrl, index) in currentAlert.handleImageUrls" 
                    :key="index"
                    class="handle-image-item"
                    @click="previewHandleImage(imageUrl)"
                  >
                    <img 
                      :src="getFullImageUrl(imageUrl)" 
                      :alt="`处理图片${index + 1}`"
                      class="handle-image-thumb"
                      @error="handleImageError"
                    />
                    <div class="image-overlay">
                      <i class="el-icon-zoom-in"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="additional-info" v-if="currentAlert.sourceId || currentAlert.sourceName">
              <div class="source-info">
                <span v-if="currentAlert.sourceType" class="source-tag">{{ currentAlert.sourceType }}</span>
                <span v-if="currentAlert.sourceName">{{ currentAlert.sourceName }}</span>
                <span v-if="currentAlert.sourceId" class="source-id">({{ currentAlert.sourceId }})</span>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 告警记录 -->
        <div v-show="currentActiveTab === 'records'" class="alert-records-container">
          <div class="search-form">
            <el-form :inline="true" :model="searchForm" class="compact-form" size="small">
              <!-- 第一行：主要搜索条件 -->
              <el-form-item>
                <el-input v-model="searchForm.mmsiOrName" placeholder="MMSI或船名" clearable size="small" style="width: 150px;"></el-input>
              </el-form-item>
              <el-form-item label="类型">
                <el-select v-model="searchForm.ruleType" placeholder="全部" clearable size="small" style="width: 120px;">
                  <el-option 
                    v-for="item in ruleTypeOptions" 
                    :key="item.value" 
                    :label="item.label" 
                    :value="item.value">
                  </el-option>
                </el-select>
              </el-form-item>
              <el-form-item>
                <el-date-picker
                  v-model="searchForm.timeRange"
                  type="datetimerange"
                  range-separator="至"
                  start-placeholder="开始时间"
                  end-placeholder="结束时间"
                  size="small"
                  format="yyyy-MM-dd HH:mm:ss"
                  value-format="yyyy-MM-dd HH:mm:ss"
                  style="width: 350px;">
                </el-date-picker>
              </el-form-item>
              <el-form-item>
                <el-button type="primary" size="small" @click="searchAlertRecords" icon="el-icon-search">查询</el-button>
              </el-form-item>
              <el-form-item>
                <el-button size="small" @click="resetSearchForm" icon="el-icon-refresh">重置</el-button>
              </el-form-item>
              <el-form-item>
                <el-button 
                  size="small" 
                  type="text" 
                  @click="toggleExpandSearch"
                  class="expand-btn"
                >
                  {{ expandSearch ? '收起' : '更多条件' }}
                  <i :class="expandSearch ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                </el-button>
              </el-form-item>
              
              <!-- 第二行：展开的更多条件 -->
              <div v-show="expandSearch" class="expand-search-row">
                <el-form-item label="级别">
                  <el-select v-model="searchForm.alertLevel" placeholder="全部" clearable size="small" style="width: 120px;">
                    <el-option 
                      v-for="item in alertLevelOptions" 
                      :key="getAlertLevelValue(item.value)" 
                      :label="item.label" 
                      :value="getAlertLevelValue(item.value)">
                    </el-option>
                  </el-select>
                </el-form-item>
                <el-form-item label="状态">
                  <el-select v-model="searchForm.status" placeholder="全部" clearable size="small" style="width: 120px;">
                    <el-option label="未处理" :value="0"></el-option>
                    <el-option label="已处理" :value="1"></el-option>
                    <el-option label="已忽略" :value="2"></el-option>
                  </el-select>
                </el-form-item>
              </div>
            </el-form>
          </div>
          
          <!-- 批量操作栏 -->
          <div class="batch-action-bar" v-show="selectedAlerts.length > 0">
            <span class="selected-info">
              <i class="el-icon-info"></i>
              已选择 <strong>{{ selectedAlerts.length }}</strong> 条记录
            </span>
            <el-button 
              type="danger" 
              size="mini" 
              icon="el-icon-delete"
              @click="confirmBatchDelete"
              :loading="submitLoading"
            >
              批量删除
            </el-button>
            <el-button 
              size="mini" 
              @click="clearSelection"
            >
              取消选择
            </el-button>
          </div>

          <el-table
            ref="alertTable"
            :data="alertRecords"
            border
            stripe
            style="width: 100%"
            v-loading="loading"
            size="small"
            height="320"
            @selection-change="handleSelectionChange"
          >
            <el-table-column type="selection" width="45" fixed></el-table-column>
            <el-table-column prop="sourceId" label="MMSI" width="90" fixed></el-table-column>
            <el-table-column prop="sourceName" label="船名" min-width="120" show-overflow-tooltip></el-table-column>
            <el-table-column prop="alertTime" label="告警时间" width="150">
              <template slot-scope="scope">
                {{ formatTime(scope.row.alertTime) }}
              </template>
            </el-table-column>
            <el-table-column prop="alertLevel" label="级别" width="60">
              <template slot-scope="scope">
                <el-tag :type="getAlertLevelType(scope.row.alertLevel)" size="mini">{{ getAlertLevelType(scope.row.alertLevel) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="ruleType" label="类型" width="60">
              <template slot-scope="scope">
                {{ getRuleTypeText(scope.row.ruleType) }}
              </template>
            </el-table-column>
            <el-table-column prop="alertMessage" label="告警内容" min-width="200">
              <template slot-scope="scope">
                <el-tooltip :content="scope.row.alertMessage" placement="top" :disabled="!scope.row.alertMessage">
                  <div class="alert-message-cell">{{ scope.row.alertMessage }}</div>
                </el-tooltip>
              </template>
            </el-table-column>
            <el-table-column prop="status" label="状态" width="70">
              <template slot-scope="scope">
                <el-tag :type="getStatusType(scope.row.status)" size="mini">{{ getStatusText(scope.row.status) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="150" fixed="right">
              <template slot-scope="scope">
                <div class="operation-buttons">
                  <el-button 
                    size="mini" 
                    type="primary" 
                    icon="el-icon-view"
                    @click="handleCommand({action: 'view', row: scope.row})"
                  >
                    查看
                  </el-button>
                  <el-button 
                    size="mini" 
                    type="success" 
                    icon="el-icon-location"
                    :disabled="!scope.row.sourceId"
                    @click="handleCommand({action: 'track', row: scope.row})"
                  >
                    轨迹
                  </el-button>
                </div>
              </template>
            </el-table-column>
          </el-table>
          
          <div class="pagination-container">
            <el-pagination
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
              :current-page="pagination.pageNum"
              :page-sizes="[10, 20, 50, 100]"
              :page-size="pagination.pageSize"
              layout="total, sizes, prev, pager, next, jumper"
              :total="pagination.total"
              small
            >
            </el-pagination>
          </div>
        </div>

        <!-- 新增告警（手动增加告警记录） -->
        <div v-show="currentActiveTab === 'add'" class="alert-add-container">
          <div class="add-info-card">
            <el-form
              ref="manualForm"
              :model="manualForm"
              :rules="manualRules"
              label-width="110px"
              label-position="left"
              size="small"
            >
              <el-form-item label="预警类型" prop="alertType">
                <el-select v-model="manualForm.alertType" placeholder="请选择预警类型" style="width: 100%;">
                  <el-option
                    v-for="item in alertTypeOptions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value">
                  </el-option>
                </el-select>
              </el-form-item>
              <el-form-item label="预警级别" prop="alertLevel">
                <el-select v-model="manualForm.alertLevel" placeholder="请选择预警级别" style="width: 100%;">
                  <el-option label="低" :value="1"></el-option>
                  <el-option label="中" :value="2"></el-option>
                  <el-option label="高" :value="3"></el-option>
                </el-select>
              </el-form-item>
              <el-form-item label="船舶MMSI" prop="vesselMmsi">
                <el-input v-model="manualForm.vesselMmsi" placeholder="请输入船舶MMSI（可空）"></el-input>
              </el-form-item>
              <el-form-item label="区域" prop="areaId">
                <el-select v-model="manualForm.areaId" placeholder="请选择区域（可空）" clearable style="width: 100%;">
                  <el-option
                    v-for="item in areas"
                    :key="item.id"
                    :label="item.areaName"
                    :value="item.id">
                  </el-option>
                </el-select>
              </el-form-item>
              <el-form-item label="预警描述" prop="description">
                <el-input
                  type="textarea"
                  :rows="4"
                  v-model="manualForm.description"
                  placeholder="请输入预警描述">
                </el-input>
              </el-form-item>
            </el-form>
            <div class="add-actions">
              <el-button size="small" @click="resetManualForm">重置</el-button>
              <el-button
                type="primary"
                size="small"
                :loading="manualSaving"
                icon="el-icon-plus"
                @click="handleSubmitManual">
                新增告警记录
              </el-button>
            </div>
          </div>
        </div>

        <!-- 处理记录（该桥梁的处置明细） -->
        <div v-show="currentActiveTab === 'handle'" class="alert-records-container">
          <div class="search-form">
            <el-form :inline="true" :model="handleSearchForm" class="compact-form" size="small">
              <el-form-item>
                <el-input v-model="handleSearchForm.mmsiOrName" placeholder="MMSI或船名" clearable size="small" style="width: 150px;"></el-input>
              </el-form-item>
              <el-form-item label="类型">
                <el-select v-model="handleSearchForm.ruleType" placeholder="全部" clearable size="small" style="width: 120px;">
                  <el-option
                    v-for="item in ruleTypeOptions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value">
                  </el-option>
                </el-select>
              </el-form-item>
              <el-form-item label="处理状态">
                <el-select v-model="handleSearchForm.status" size="small" style="width: 110px;">
                  <el-option label="已处理" :value="1"></el-option>
                  <el-option label="已忽略" :value="2"></el-option>
                </el-select>
              </el-form-item>
              <el-form-item>
                <el-date-picker
                  v-model="handleSearchForm.timeRange"
                  type="datetimerange"
                  range-separator="至"
                  start-placeholder="开始时间"
                  end-placeholder="结束时间"
                  size="small"
                  format="yyyy-MM-dd HH:mm:ss"
                  value-format="yyyy-MM-dd HH:mm:ss"
                  style="width: 350px;">
                </el-date-picker>
              </el-form-item>
              <el-form-item>
                <el-button type="primary" size="small" @click="searchHandleRecords" icon="el-icon-search">查询</el-button>
              </el-form-item>
              <el-form-item>
                <el-button size="small" @click="resetHandleSearch" icon="el-icon-refresh">重置</el-button>
              </el-form-item>
            </el-form>
          </div>

          <el-table
            :data="handleRecords"
            border
            stripe
            style="width: 100%"
            v-loading="handleLoading"
            size="small"
            height="320"
          >
            <el-table-column prop="sourceId" label="MMSI" width="90" fixed></el-table-column>
            <el-table-column prop="sourceName" label="船名" min-width="110" show-overflow-tooltip></el-table-column>
            <el-table-column prop="alertTime" label="告警时间" width="150">
              <template slot-scope="scope">
                {{ formatTime(scope.row.alertTime) }}
              </template>
            </el-table-column>
            <el-table-column prop="ruleType" label="类型" width="70">
              <template slot-scope="scope">
                {{ getRuleTypeText(scope.row.ruleType) }}
              </template>
            </el-table-column>
            <el-table-column prop="alertMessage" label="告警内容" min-width="180">
              <template slot-scope="scope">
                <el-tooltip :content="scope.row.alertMessage" placement="top" :disabled="!scope.row.alertMessage">
                  <div class="alert-message-cell">{{ scope.row.alertMessage }}</div>
                </el-tooltip>
              </template>
            </el-table-column>
            <el-table-column label="处理时间" width="150">
              <template slot-scope="scope">
                {{ scope.row.handleTime ? formatTime(scope.row.handleTime) : '—' }}
              </template>
            </el-table-column>
            <el-table-column label="处理人" width="110">
              <template slot-scope="scope">
                {{ getHandlerName(scope.row) }}
              </template>
            </el-table-column>
            <el-table-column label="处理结果" min-width="150" show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.handleResult || '—' }}
              </template>
            </el-table-column>
            <el-table-column label="截图" width="130" align="center">
              <template slot-scope="scope">
                <div v-if="scope.row._imageUrls && scope.row._imageUrls.length" class="handle-thumbs">
                  <el-image
                    v-for="(url, idx) in scope.row._imageUrls.slice(0, 3)"
                    :key="idx"
                    :src="url"
                    :preview-src-list="scope.row._imageUrls"
                    :initial-index="idx"
                    fit="cover"
                    class="handle-thumb">
                  </el-image>
                  <span v-if="scope.row._imageUrls.length > 3" class="thumb-more">+{{ scope.row._imageUrls.length - 3 }}</span>
                </div>
                <span v-else class="handle-no-image">—</span>
              </template>
            </el-table-column>
            <el-table-column label="状态" width="70" fixed="right">
              <template slot-scope="scope">
                <el-tag :type="getStatusType(scope.row.status)" size="mini">{{ getStatusText(scope.row.status) }}</el-tag>
              </template>
            </el-table-column>
          </el-table>

          <div class="pagination-container">
            <el-pagination
              @size-change="handleHandleSizeChange"
              @current-change="handleHandleCurrentChange"
              :current-page="handlePagination.pageNum"
              :page-sizes="[10, 20, 50, 100]"
              :page-size="handlePagination.pageSize"
              layout="total, sizes, prev, pager, next, jumper"
              :total="handlePagination.total">
            </el-pagination>
          </div>
        </div>
      </div>
      
      <!-- 批量处理弹窗 -->
      <el-dialog
        title="批量处理告警"
        :visible.sync="batchHandleVisible"
        width="40%"
        append-to-body
        :modal-append-to-body="false"
      >
        <el-form :model="batchHandleForm" label-width="80px">
          <el-form-item label="处理方式">
            <el-radio-group v-model="batchHandleForm.status">
              <el-radio :label="1">已处理</el-radio>
              <el-radio :label="2">已忽略</el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="处理结果">
            <el-input type="textarea" v-model="batchHandleForm.handleResult" :rows="3" placeholder="请输入处理结果"></el-input>
          </el-form-item>
        </el-form>
        <span slot="footer" class="dialog-footer">
          <el-button @click="batchHandleVisible = false">取消</el-button>
          <el-button type="primary" @click="batchHandleAlerts" :loading="submitLoading">确定</el-button>
        </span>
      </el-dialog>
      
      <!-- 快速处理弹窗 -->
      <el-dialog
        title="快速处理告警"
        :visible.sync="quickHandleVisible"
        width="40%"
        append-to-body
        :modal-append-to-body="false"
      >
        <el-form :model="quickHandleForm" label-width="80px">
          <el-form-item label="处理方式">
            <el-radio-group v-model="quickHandleForm.status">
              <el-radio :label="1">已处理</el-radio>
              <el-radio :label="2">已忽略</el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="处理结果">
            <el-input type="textarea" v-model="quickHandleForm.handleResult" :rows="3" placeholder="请输入处理结果"></el-input>
          </el-form-item>
        </el-form>
        <span slot="footer" class="dialog-footer">
          <el-button @click="quickHandleVisible = false">取消</el-button>
          <el-button type="primary" @click="confirmQuickHandle" :loading="submitLoading">确定</el-button>
        </span>
      </el-dialog>
      
      <!-- 处理图片预览弹窗 -->
      <el-dialog
        title="处理图片"
        :visible.sync="handleImagePreviewVisible"
        width="60%"
        append-to-body
        :modal-append-to-body="false"
      >
        <div class="handle-image-preview-container">
          <img 
            :src="currentPreviewImageUrl" 
            alt="处理图片预览" 
            class="handle-image-preview"
            @error="handleImageError"
          />
        </div>
      </el-dialog>
  </draggable-panel>
</template>
  
    <script>
  import { 
    getAlertRecordById, 
    getAlertRecordList,
    getHandledAlertRecordList,
    handleAlertRecord,
    batchHandleAlertRecords
  } from '@/api/alert-record';
  
  import { getAlertLevelOptions, getAlertMethodOptions, getRuleTypeOptions, sendManualAlert } from '@/api/alert';
  import { getAreasByBridgeId } from '@/api/area';
  import { formatLatitude, formatLongitude } from '@/utils/coordinate';
  import { formatDate } from '@/utils/dateUtils';
  import DraggablePanel from '@/components/common/DraggablePanel.vue'
  import ImageUpload from '@/components/common/ImageUpload.vue'
  import config from '@/config/app-config'

export default {
  name: 'AlertDetailPanel',
  components: {
    DraggablePanel,
    ImageUpload
  },
    props: {
      visible: {
        type: Boolean,
        default: false
      },
      alertData: {
        type: Object,
        default: () => ({})
      },
      bridgeId: {
        type: [Number, String], // 修改为接受Number或String类型
        default: null
      },
      activeTab: {
        type: String,
        default: 'records'
      },
      zIndex: {
        type: Number,
        default: 1000
      }
    },
    data() {
      return {
        title: '告警管理',
        icon: 'el-icon-warning',
        tabs: [
          { key: 'records', label: '告警记录' },
          { key: 'detail', label: '告警详情' },
          { key: 'add', label: '新增告警' },
          { key: 'handle', label: '处理记录' }
        ],
        isExpanded: false, // 展开/收起状态
        // 不再设置默认值，而是使用计算属性
        currentAlert: {},
        alertRecords: [],
        selectedAlerts: [], // 批量删除选中的告警
        selectedUnhandledAlerts: [],
        handleForm: {
          status: 1,
          handleResult: '',
          attachments: []
        },
        batchHandleForm: {
          status: 1,
          handleResult: ''
        },
        quickHandleForm: {
          status: 1,
          handleResult: ''
        },
        batchHandleVisible: false,
        quickHandleVisible: false,
        quickHandleAlertId: null,
        handleImagePreviewVisible: false,
        currentPreviewImageUrl: '',
        searchForm: {
          mmsiOrName: '',
          alertLevel: '',
          ruleType: '',
          status: '',
          timeRange: null
        },
        expandSearch: false,
        pagination: {
          total: 0,
          pageNum: 1,
          pageSize: 10
        },
        alertLevelOptions: [],
        alertMethodOptions: [],
        ruleTypeOptions: [],
        // 新增告警（手动增加告警记录）
        areas: [],
        alertTypeOptions: [],
        manualSaving: false,
        // 处理记录（该桥梁已处置的告警明细）
        handleRecords: [],
        handleLoading: false,
        handleSearchForm: {
          mmsiOrName: '',
          ruleType: '',
          status: 1,
          timeRange: null
        },
        handlePagination: {
          total: 0,
          pageNum: 1,
          pageSize: 10
        },
        manualForm: {
          alertType: '',
          alertLevel: 3,
          vesselMmsi: '',
          areaId: null,
          description: ''
        },
        manualRules: {
          alertType: [{ required: true, message: '请选择预警类型', trigger: 'change' }],
          alertLevel: [{ required: true, message: '请选择预警级别', trigger: 'change' }],
          description: [{ required: true, message: '请输入预警描述', trigger: 'blur' }]
        },
        loading: false,
        submitLoading: false,
        internalActiveTab: 'records' // 内部状态，默认为记录标签页
      };
    },
    computed: {
      // 计算属性，用于获取当前活动的标签页
      currentActiveTab() {
        return this.internalActiveTab;
      }
    },
    mounted() {
      // 将面板提升到 body，避免被页面内浮层层叠上下文遮挡
      if (this.$el && this.$el.parentNode !== document.body) {
        document.body.appendChild(this.$el);
      }
    },
    beforeDestroy() {
      // 组件销毁时清理挂载节点
      if (this.$el && this.$el.parentNode === document.body) {
        document.body.removeChild(this.$el);
      }
    },
    watch: {
      visible(val) {
        if (val) {
          // 当弹窗显示时，加载数据
          this.init();
        } else {
          // 重置表单
          this.resetForms();
        }
      },
      alertData: {
        handler(val) {
          if (val && val.id) {
            this.currentAlert = { ...val };
            // 如果是告警详情标签页，获取最新的告警详情
            if (this.currentActiveTab === 'detail') {
              this.getAlertDetail(val.id);
            }
          }
        },
        deep: true
      },
      // 监听activeTab属性变化
      activeTab: {
        handler(newVal) {
          if (newVal && newVal !== this.internalActiveTab) {
            this.internalActiveTab = newVal;
            this.handleTabClick(newVal);
          }
        },
        immediate: true
      },
      // 监听内部activeTab变化
      internalActiveTab(newVal) {
        this.handleTabClick(newVal);
        // 通知父组件标签页已变更
        this.$emit('update:active-tab', newVal);
      },
      // 桥梁切换时重新加载新增告警所需数据
      bridgeId() {
        this.fetchAreas();
      }
    },
    methods: {
      // 添加辅助方法转换bridgeId
      /**
       * 将bridgeId转换为数字类型
       */
      getBridgeIdAsNumber() {
        if (!this.bridgeId) return null;
        return typeof this.bridgeId === 'string' ? parseInt(this.bridgeId, 10) : this.bridgeId;
      },
      
      /**
       * 初始化
       */
      init() {
        // 设置默认时间范围
        if (!this.searchForm.timeRange) {
          this.searchForm.timeRange = this.getDefaultTimeRange();
        }
        if (!this.handleSearchForm.timeRange) {
          this.handleSearchForm.timeRange = this.getDefaultTimeRange();
        }
        
        this.initData();
        // 获取当前告警详情
        if (this.alertData && this.alertData.id) {
          this.getAlertDetail(this.alertData.id);
        }
        // 加载新增告警所需数据（区域、预警类型）
        this.fetchAreas();
        this.fetchAlertTypeOptions();
                
        // 如果是告警记录标签页，加载告警记录
        if (this.currentActiveTab === 'records') {
          this.searchAlertRecords();
        }
      },
      
      async initData() {  
        try {
          this.loading = true;
          // 并行加载所有选项数据
          await Promise.all([
            this.fetchRuleTypeOptions(),
            this.fetchAlertLevelOptions(),
            this.fetchAlertMethodOptions()
          ]);
        } catch (error) {
          console.error('初始化数据失败:', error);
          this.$message.error('加载数据失败，请刷新重试');
        } finally {
          this.loading = false;
        }
      },
      
      /**
       * 格式化坐标显示
       */
      formatCoordinate(longitude, latitude) {
        if (!longitude || !latitude) return '未知位置';
        return `${formatLongitude(longitude)} ${formatLatitude(latitude)}`;
      },
  
      /**
       * 格式化时间显示
       */
      formatTime(time) {
        return formatDate(time);
      },
      
      /**
       * 格式化日期时间
       * @param {Date} date - 日期对象
       * @returns {string} 格式化后的日期时间字符串
       */
      formatDateTime(date) {
        if (!date || !(date instanceof Date)) {
          return '';
        }
        
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hour = String(date.getHours()).padStart(2, '0');
        const minute = String(date.getMinutes()).padStart(2, '0');
        const second = String(date.getSeconds()).padStart(2, '0');
        
        return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
      },
      
      /**
       * 获取告警详情
       */
      async getAlertDetail(id) {
        if (!id) return;
        
        try {
          this.loading = true;
          const res = await getAlertRecordById(id);
          if (res.code === 200) {
            this.currentAlert = res.data;
          } else {
            this.$message.error(res.message || '获取告警详情失败');
          }
        } catch (error) {
          console.error('获取告警详情失败', error);
          this.$message.error('获取告警详情失败，请稍后重试');
        } finally {
          this.loading = false;
        }
      },
      
      /**
       * 搜索告警记录
       */
      async searchAlertRecords() {
        const bridgeId = this.getBridgeIdAsNumber();
        if (!bridgeId) return;
        
        const params = {
          pageNum: this.pagination.pageNum,
          pageSize: this.pagination.pageSize,
          bridgeId: bridgeId,
          mmsiOrName: this.searchForm.mmsiOrName || undefined,
          alertLevel: this.searchForm.alertLevel || undefined,
          ruleType: this.searchForm.ruleType || undefined,
          status: this.searchForm.status !== '' ? this.searchForm.status : undefined
        };
        
        // 处理时间范围
        if (this.searchForm.timeRange && this.searchForm.timeRange.length === 2) {
          params.startTimeStr = this.searchForm.timeRange[0];
          params.endTimeStr = this.searchForm.timeRange[1];
        }

        try {
          this.loading = true;
          const res = await getAlertRecordList(params);
          if (res.code === 200) {
            this.alertRecords = res.data.list || [];
            this.pagination.total = res.data.total || 0;
          } else {
            this.$message.error(res.message || '查询告警记录失败');
          }
        } catch (error) {
          console.error('查询告警记录失败', error);
          this.$message.error('查询告警记录失败，请稍后重试');
        } finally {
          this.loading = false;
        }
      },
      
      /**
       * 重置搜索表单
       */
      resetSearchForm() {
        const defaultTimeRange = this.getDefaultTimeRange();
        this.searchForm = {
          mmsiOrName: '',
          alertLevel: '',
          ruleType: '',
          status: '',
          timeRange: defaultTimeRange
        };
        this.pagination.pageNum = 1;
        this.searchAlertRecords();
      },

      /**
       * 查询该桥梁的处理记录（已处置的告警明细）
       */
      async searchHandleRecords() {
        const bridgeId = this.getBridgeIdAsNumber();
        if (!bridgeId) return;

        const params = {
          pageNum: this.handlePagination.pageNum,
          pageSize: this.handlePagination.pageSize,
          bridgeId: bridgeId,
          mmsiOrName: this.handleSearchForm.mmsiOrName || undefined,
          ruleType: this.handleSearchForm.ruleType || undefined,
          status: this.handleSearchForm.status !== '' ? this.handleSearchForm.status : undefined
        };
        if (this.handleSearchForm.timeRange && this.handleSearchForm.timeRange.length === 2) {
          params.startTimeStr = this.handleSearchForm.timeRange[0];
          params.endTimeStr = this.handleSearchForm.timeRange[1];
        }

        try {
          this.handleLoading = true;
          const res = await getHandledAlertRecordList(params);
          if (res.code === 200) {
            const list = (res.data && res.data.list) || [];
            // 预计算图片完整地址（含登录 token），避免渲染时重复拼接
            list.forEach(row => {
              const urls = (row.handleImageUrls && row.handleImageUrls.length)
                ? row.handleImageUrls
                : (row.handleImages ? String(row.handleImages).split(',').filter(Boolean)
                    .map(name => `/api/image-service/handled/${String(name).trim()}`) : []);
              row._imageUrls = urls.map(u => this.getFullImageUrl(u));
            });
            this.handleRecords = list;
            this.handlePagination.total = (res.data && res.data.total) || 0;
          } else {
            this.$message.error(res.message || '查询处理记录失败');
          }
        } catch (error) {
          console.error('查询处理记录失败', error);
          this.$message.error('查询处理记录失败，请稍后重试');
        } finally {
          this.handleLoading = false;
        }
      },

      /**
       * 重置处理记录搜索条件
       */
      resetHandleSearch() {
        this.handleSearchForm = {
          mmsiOrName: '',
          ruleType: '',
          status: 1,
          timeRange: this.getDefaultTimeRange()
        };
        this.handlePagination.pageNum = 1;
        this.searchHandleRecords();
      },

      /**
       * 处理记录分页大小变化
       */
      handleHandleSizeChange(size) {
        this.handlePagination.pageSize = size;
        this.searchHandleRecords();
      },

      /**
       * 处理记录页码变化
       */
      handleHandleCurrentChange(page) {
        this.handlePagination.pageNum = page;
        this.searchHandleRecords();
      },

      /**
       * 处理人显示名：优先中文名，其次处理器标识（系统标识转中文），最后回退用户ID
       */
      getHandlerName(row) {
        if (!row) return '—';
        if (row.handleUserName) return row.handleUserName;
        if (row.handleUser) return `用户${row.handleUser}`;
        if (row.processor) return this.formatProcessor(row.processor) || '—';
        return '—';
      },

      /**
       * 处理器标识转展示文案：SYSTEM_AUTO → 系统自动处理
       */
      formatProcessor(value) {
        if (value === null || value === undefined) return '';
        const raw = String(value).trim();
        if (!raw) return '';
        const map = {
          SYSTEM_AUTO: '系统自动处理',
          SYSTEM: '系统自动处理',
          AUTO: '系统自动处理',
          MANUAL: '人工处理'
        };
        return map[raw.toUpperCase()] || raw;
      },

      /**
       * 切换展开搜索条件
       */
      toggleExpandSearch() {
        this.expandSearch = !this.expandSearch;
      },

      /**
       * 获取默认时间范围（当天零点到现在）
       */
      getDefaultTimeRange() {
        const now = new Date();
        const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
        
        return [
          this.formatDateTime(startOfDay),
          this.formatDateTime(now)
        ];
      },

      
      /**
       * 重置所有表单
       */
      resetForms() {
        this.handleForm = {
          status: 1,
          handleResult: '',
          attachments: []
        };
        this.batchHandleForm = {
          status: 1,
          handleResult: ''
        };
        this.quickHandleForm = {
          status: 1,
          handleResult: ''
        };
        this.selectedAlerts = [];
        this.selectedUnhandledAlerts = [];
      },
      
      /**
       * 处理告警
       */
      async handleAlert() {
        try {
          this.submitLoading = true;
          
          // 获取图片文件
          const imageFiles = this.$refs.imageUpload ? this.$refs.imageUpload.getFilesForSubmit() : [];
          
          console.log('告警处理 - 当前表单数据:', {
            alertId: this.currentAlert.id,
            status: this.handleForm.status,
            handleResult: this.handleForm.handleResult,
            imageCount: imageFiles.length,
            images: imageFiles
          });
          
          // 验证文件是否正确获取
          if (imageFiles.length > 0) {
            console.log('图片文件详情:');
            imageFiles.forEach((file, index) => {
              console.log(`图片 ${index}:`, {
                name: file.name,
                size: file.size,
                type: file.type,
                lastModified: file.lastModified,
                isFile: file instanceof File
              });
            });
          }
          
          const res = await handleAlertRecord(
            this.currentAlert.id, 
            this.handleForm.status, 
            this.handleForm.handleResult,
            imageFiles
          );
          
          if (res.code === 200) {
            this.$message.success('处理成功');
            // 通知父组件告警已处理
            this.$emit('alert-handled');
            // 从"告警详情"切换到"告警记录"标签页
            this.internalActiveTab = 'records';
            // 刷新告警记录列表
            this.searchAlertRecords();
          } else {
            this.$message.error(res.message || '处理失败');
          }
        } catch (error) {
          console.error('处理告警失败', error);
          this.$message.error('处理告警失败');
        } finally {
          this.submitLoading = false;
        }
      },
      
      /**
       * 批量处理告警
       */
      async batchHandleAlerts() {
        if (this.selectedUnhandledAlerts.length === 0) {
          this.$message.warning('请选择要处理的告警');
          return;
        }
        
        try {
          this.submitLoading = true;
          const ids = this.selectedUnhandledAlerts.map(item => item.id);
          const res = await batchHandleAlertRecords(
            ids, 
            this.batchHandleForm.status, 
            this.batchHandleForm.handleResult
          );
          
          if (res.code === 200) {
            this.$message.success('批量处理成功');
            this.batchHandleVisible = false;
            // 通知父组件告警已处理
            this.$emit('alert-handled');
          } else {
            this.$message.error(res.message || '批量处理失败');
          }
        } catch (error) {
          console.error('批量处理告警失败', error);
          this.$message.error('批量处理告警失败');
        } finally {
          this.submitLoading = false;
        }
      },
      
      /**
       * 快速处理告警
       */
      quickHandleAlert(row) {
        this.quickHandleAlertId = row.id;
        this.quickHandleVisible = true;
      },
      
      /**
       * 确认快速处理
       */
      async confirmQuickHandle() {
        try {
          this.submitLoading = true;
          const res = await handleAlertRecord(
            this.quickHandleAlertId, 
            this.quickHandleForm.status, 
            this.quickHandleForm.handleResult
          );
          
          if (res.code === 200) {
            this.$message.success('处理成功');
            this.quickHandleVisible = false;
            // 通知父组件告警已处理
            this.$emit('alert-handled');
          } else {
            this.$message.error(res.message || '处理失败');
          }
        } catch (error) {
          console.error('处理告警失败', error);
          this.$message.error('处理告警失败');
        } finally {
          this.submitLoading = false;
        }
      },
      
      /**
       * 删除告警记录
       */
      async confirmDeleteAlert() {
        if (!this.currentAlert || !this.currentAlert.id) return;
        
        try {
          // 显示确认对话框
          await this.$confirm('确定要删除这条告警记录吗？此操作不可撤销。', '删除确认', {
            confirmButtonText: '确定删除',
            cancelButtonText: '取消',
            type: 'warning',
            confirmButtonClass: 'el-button--danger'
          });
          
          this.submitLoading = true;
          const { deleteAlertRecord } = await import('@/api/alert-record');
          const res = await deleteAlertRecord(this.currentAlert.id);
          
          if (res.code === 200) {
            this.$message.success('删除成功');
            // 通知父组件告警已删除
            this.$emit('alert-deleted', this.currentAlert.id);
            // 关闭面板
            this.handleClose();
          } else {
            this.$message.error(res.message || '删除失败');
          }
        } catch (error) {
          if (error === 'cancel') {
            // 用户取消删除
            return;
          }
          console.error('删除告警记录失败:', error);
          this.$message.error('删除失败，请稍后重试');
        } finally {
          this.submitLoading = false;
        }
      },
      
      /**
       * 处理表格选择变化
       */
      handleSelectionChange(selection) {
        this.selectedAlerts = selection;
      },
      
      /**
       * 清除选择
       */
      clearSelection() {
        this.$refs.alertTable.clearSelection();
        this.selectedAlerts = [];
      },
      
      /**
       * 批量删除告警记录
       */
      async confirmBatchDelete() {
        if (this.selectedAlerts.length === 0) {
          this.$message.warning('请选择要删除的告警记录');
          return;
        }
        
        try {
          // 显示确认对话框
          await this.$confirm(
            `确定要删除选中的 ${this.selectedAlerts.length} 条告警记录吗？此操作不可撤销。`, 
            '批量删除确认', 
            {
              confirmButtonText: '确定删除',
              cancelButtonText: '取消',
              type: 'warning',
              confirmButtonClass: 'el-button--danger'
            }
          );
          
          this.submitLoading = true;
          const { batchDeleteAlertRecords } = await import('@/api/alert-record');
          const ids = this.selectedAlerts.map(item => item.id);
          const res = await batchDeleteAlertRecords(ids);
          
          if (res.code === 200) {
            this.$message.success(`成功删除 ${ids.length} 条告警记录`);
            // 清除选择
            this.clearSelection();
            // 刷新告警记录列表（不关闭面板）
            this.searchAlertRecords();
          } else {
            this.$message.error(res.message || '批量删除失败');
          }
        } catch (error) {
          if (error === 'cancel') {
            // 用户取消删除
            return;
          }
          console.error('批量删除告警记录失败:', error);
          this.$message.error('批量删除失败，请稍后重试');
        } finally {
          this.submitLoading = false;
        }
      },
      
      /**
       * 查看告警详情
       */
      viewAlertDetail(row) {
        this.currentAlert = { ...row };
        this.internalActiveTab = 'detail'; // 切换内部状态
      },
      
          /**
     * 显示船舶轨迹
     * @param {Object} alert - 告警记录
     */
    async showShipTrack(alert) {
      if (!alert || !alert.sourceId) {
        this.$message.warning('该告警记录缺少船舶MMSI，无法查看轨迹');
        return;
      }
      
      try {
        this.$message.info('正在查询船舶轨迹数据...');
        
        // 计算时间范围：事件前后各1小时
        const alertTime = new Date(alert.alertTime);
        const startTime = new Date(alertTime.getTime() - 60 * 60 * 1000); // 1小时前
        const endTime = new Date(alertTime.getTime() + 60 * 60 * 1000); // 1小时后
        
        console.log('告警管理：开始查询船舶轨迹', {
          mmsi: alert.sourceId,
          sourceName: alert.sourceName,
          alertTime: alert.alertTime,
          startTime: this.formatDateTime(startTime),
          endTime: this.formatDateTime(endTime)
        });
        
        // 直接调用API获取轨迹数据
        const { searchVesselTrajectory } = await import('@/api/vessel-trajectory');
        const params = {
          keyword: alert.sourceId,
          startTime: this.formatDateTime(startTime),
          endTime: this.formatDateTime(endTime),
          pageNum: 1,
          pageSize: 1000 // 获取足够多的数据
        };
        
        const res = await searchVesselTrajectory(params);
        
        if (res.code === 200 && res.data && res.data.list) {
          console.log('轨迹数据查询成功，原始数据条数:', res.data.list.length);
          
          // 发送事件，包含MMSI和完整的响应数据
          this.$emit('show-ship-track-with-data', alert.sourceId, {
            trajectoryData: res.data,
            alertData: alert,
            timeRange: {
              startTime: this.formatDateTime(startTime),
              endTime: this.formatDateTime(endTime)
            },
            useLatestRecords: true,
            maxRecords: 10
          });
        } else {
          this.$message.error('查询轨迹数据失败：' + (res.message || '未知错误'));
        }
      } catch (error) {
        console.error('查询船舶轨迹失败:', error);
        this.$message.error('查询船舶轨迹失败，请检查网络连接');
      }
    },
      
      /**
       * 处理未处理告警选择变化
       */
      handleUnhandledSelectionChange(selection) {
        this.selectedUnhandledAlerts = selection;
      },
      
      /**
       * 处理标签页切换
       */
      handleTabClick(tabName) {
        if (tabName === 'records') {
          this.searchAlertRecords();
        }
      },
      
      /**
       * 处理关闭事件
       */
          handleClose() {
      this.$emit('update:visible', false);
    },

    handleTabChange(tabKey) {
      this.internalActiveTab = tabKey;
      // 切到「处理记录」时按需加载该桥梁的处置明细
      if (tabKey === 'handle') {
        if (!this.handleSearchForm.timeRange) {
          this.handleSearchForm.timeRange = this.getDefaultTimeRange();
        }
        this.handlePagination.pageNum = 1;
        this.searchHandleRecords();
      }
    },
    
    handlePanelFocus() {
      this.$emit('panel-focus');
    },
    
    /**
     * 处理展开/收起切换
     */
    handleExpandToggle(expanded) {
      this.isExpanded = expanded;
    },
      
      /**
       * 处理分页大小变化
       */
      handleSizeChange(size) {
        this.pagination.pageSize = size;
        this.searchAlertRecords();
      },
      
      /**
       * 处理当前页变化
       */
      handleCurrentChange(page) {
        this.pagination.pageNum = page;
        this.searchAlertRecords();
      },
      
      /**
       * 获取告警级别对应的类型
       */
      getAlertLevelType(level) {
        var levelKey= "INFO"
        if(level === 1){
          levelKey = "LOW"
        }else if(level === 2){
          levelKey = "INFO"
        }else if(level === 3){
          levelKey = "WARNING"
        }else if(level === 4){
          levelKey = "DANGER"
        }else if(level === 5){
          levelKey = "CRITICAL"
        }
        var levelText = this.getAlertLevelText(levelKey);
        return levelText;
      },

      
      /**
       * 获取告警级别对应的值
       */
       getAlertLevelValue(level) {
        var levelValue= 2
        if(level =="INFO"){
          levelValue = 2
        }else if(level === "WARNING"){
          levelValue = 3
        }else if(level === "DANGER"){
          levelValue = 4
        }
        else if(level === "CRITICAL"){
          levelValue = 5
        }
        // console.log(level+":"+levelValue);
        return levelValue;
      },
  
      /**
       * 获取告警级别对应的文本
       */
      getAlertLevelText(level) {
        const option = this.alertLevelOptions.find(item => item.value === level);
        if (option) {
          return option.label;
        }
        
        // 如果没有找到匹配的选项，使用默认映射
        switch (level) {
          case 'LOW':
            return '低';
          case 'INFO':
            return '信息';
          case 'WARNING':
            return '警告';
          case 'DANGER':
            return '危险';
          case 'CRITICAL':
            return '严重';
          default:
            return level || '未知';
        }
      },
  
      /**
       * 获取告警类型对应的文本
       */
      getRuleTypeText(type) {
        const option = this.ruleTypeOptions.find(item => item.value === type);
        return option ? option.label : type;
      },
      
      /**
       * 获取状态对应的类型
       */
      getStatusType(status) {
        switch (status) {
          case 0:
            return 'warning';
          case 1:
            return 'success';
          case 2:
            return 'info';
          default:
            return 'info';
        }
      },
      
      /**
       * 获取状态对应的文本
       */
      getStatusText(status) {
        switch (status) {
          case 0:
            return '未处理';
          case 1:
            return '已处理';
          case 2:
            return '已忽略';
          default:
            return '未知';
        }
      },
      
      // 获取告警类型选项
      async fetchRuleTypeOptions() {
        try {
          const res = await getRuleTypeOptions();
          if (res.code === 200 && Array.isArray(res.data)) {
            //把值CORSS替换为CROSSING
            this.ruleTypeOptions = res.data.map(item => ({
              label: item.label|| item.value,
              value: item.value.replace('CROSS', 'CROSSING')  || item.key
            }));
          } else {
            console.error('获取告警类型选项失败:', res.message || '未知错误');
          }
        } catch (error) {
          console.error('获取告警类型选项出错:', error);
        }
      },
      
      // 获取告警级别选项
      async fetchAlertLevelOptions() {
        try {
          const res = await getAlertLevelOptions();
          if (res.code === 200 && Array.isArray(res.data)) {
            this.alertLevelOptions = res.data.map(item => ({
              label: item.label || item.value,
              value: item.value || item.key
            }));
          } else {
            console.error('获取告警级别选项失败:', res.message || '未知错误');
          }
        } catch (error) {
          console.error('获取告警级别选项出错:', error);
        }
      },
      
      // 获取告警方式选项
      async fetchAlertMethodOptions() {
        try {
          const res = await getAlertMethodOptions();
          if (res.code === 200 && Array.isArray(res.data)) {
            this.alertMethodOptions = res.data.map(item => ({
              label: item.label || item.value,
              value: item.value || item.key
            }));
          } else {
            console.error('获取告警方式选项失败:', res.message || '未知错误');
          }
        } catch (error) {
          console.error('获取告警方式选项出错:', error);
        }
      },

      // 加载区域列表（新增告警用）
      async fetchAreas() {
        const bridgeId = this.getBridgeIdAsNumber();
        if (!bridgeId) return;
        try {
          const res = await getAreasByBridgeId(bridgeId);
          if (res.code === 200) {
            this.areas = res.data || [];
          }
        } catch (error) {
          console.error('获取区域列表出错：', error);
        }
      },

      // 获取预警类型选项（新增告警用）
      async fetchAlertTypeOptions() {
        try {
          const res = await getRuleTypeOptions();
          if (res.code === 200 && Array.isArray(res.data)) {
            this.alertTypeOptions = res.data.map(item => ({
              label: item.label || item.value,
              value: (item.value || item.key).replace('CROSS', 'CROSSING')
            }));
          }
        } catch (error) {
          console.error('获取预警类型选项出错:', error);
        }
      },

      // 重置新增告警表单
      resetManualForm() {
        this.manualForm = {
          alertType: '',
          alertLevel: 3,
          vesselMmsi: '',
          areaId: null,
          description: ''
        };
        if (this.$refs.manualForm) this.$refs.manualForm.clearValidate();
      },

      // 提交新增告警（手动增加告警记录）
      handleSubmitManual() {
        this.$refs.manualForm.validate(async valid => {
          if (!valid) return;
          const bridgeId = this.getBridgeIdAsNumber();
          if (!bridgeId) {
            this.$message.warning('缺少桥梁信息，无法新增告警记录');
            return;
          }
          this.manualSaving = true;
          try {
            const data = {
              alertType: this.manualForm.alertType,
              alertLevel: this.manualForm.alertLevel,
              description: this.manualForm.description,
              vesselMmsi: this.manualForm.vesselMmsi || undefined,
              areaId: this.manualForm.areaId || undefined
            };
            const res = await sendManualAlert(bridgeId, data);
            if (res.code === 200) {
              this.$message.success('新增告警记录成功');
              this.resetManualForm();
              // 新增成功后回到"告警记录"标签页并刷新列表
              this.internalActiveTab = 'records';
              this.searchAlertRecords();
            } else {
              this.$message.error(res.message || res.msg || '新增告警记录失败');
            }
          } catch (error) {
            console.error('新增告警记录失败', error);
            this.$message.error('新增告警记录失败，请稍后重试');
          } finally {
            this.manualSaving = false;
          }
        });
      },

      /**
       * 处理表格操作命令
       */
      handleCommand({ action, row }) {
        if (action === 'view') {
          this.viewAlertDetail(row);
        } else if (action === 'track') {
          this.showShipTrack(row);
        }
      },
      
      /**
       * 获取完整的图片URL
       */
      getFullImageUrl(relativeUrl) {
        // 如果相对路径已经包含域名，直接返回
        let fullUrl;
        if (relativeUrl.startsWith('http')) {
          fullUrl = relativeUrl;
        } else {
          // 拼接完整URL - 使用API域名而不是前端域名
          const apiBaseUrl = config.api.baseURL || 'http://localhost:18080/api';
          // 移除API baseURL末尾的/api部分，因为relativeUrl已经包含/api
          const apiHost = apiBaseUrl.replace('/api', '');
          fullUrl = `${apiHost}${relativeUrl}`;
        }
        // 图片接口已启用鉴权，<img>无法携带请求头，通过URL参数携带登录token
        const token = localStorage.getItem('token');
        if (token) {
          fullUrl += (fullUrl.includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(token);
        }
        return fullUrl;
      },
      
      /**
       * 预览处理图片
       */
      previewHandleImage(imageUrl) {
        this.currentPreviewImageUrl = this.getFullImageUrl(imageUrl);
        this.handleImagePreviewVisible = true;
        console.log('预览处理图片:', this.currentPreviewImageUrl);
      },
      
      /**
       * 处理图片加载错误
       */
      handleImageError(event) {
        console.error('图片加载失败:', event.target.src);
        event.target.style.display = 'none';
        // 可以显示默认图片或错误提示
        const parent = event.target.parentNode;
        if (parent && !parent.querySelector('.image-error')) {
          const errorDiv = document.createElement('div');
          errorDiv.className = 'image-error';
          errorDiv.textContent = '图片加载失败';
          parent.appendChild(errorDiv);
        }
      }
    }
  }
  </script>
  
  <style scoped>
  /* 面板内容样式 */
  .panel-content {
    padding: 0;
    max-height: 70vh;
    overflow-y: auto;
  }
  

  
  /* 告警详情样式 */
  .alert-info-card {
    background-color: #f8f9fa;
    border-radius: 4px;
    padding: 15px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  }
  
  .alert-info-row {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 12px;
    gap: 15px;
  }
  
  .alert-info-item {
    display: flex;
    align-items: center;
  }
  
  .label {
    color: #606266;
    font-size: 13px;
    margin-right: 5px;
    white-space: nowrap;
  }
  
  .value {
    color: #303133;
    font-size: 13px;
    font-weight: 500;
  }
  
  /* 告警消息样式 */
  .alert-message {
    padding: 10px 15px;
    background-color: #fff;
    border-radius: 4px;
    font-size: 14px;
    line-height: 1.5;
    color: #303133;
    margin-bottom: 12px;
    border-left: 3px solid #409EFF;
  }
  
  /* 处理区域样式 */
  .alert-handle-area {
    margin-top: 10px;
  }
  
  .handle-form {
    display: flex;
    flex-direction: column;
    gap: 15px;
  }
  
  .handle-row {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 10px;
  }
  
  .handle-row:last-child {
    justify-content: flex-end;
  }
  
  .handle-options {
    display: flex;
    gap: 15px;
  }
  
  .handle-input {
    width: 100%;
  }

  .upload-section {
    width: 100%;
  }

  .upload-label {
    font-size: 13px;
    color: #606266;
    margin-bottom: 8px;
  }

  .handle-upload {
    width: 100%;
  }
  
  /* 已处理信息样式 */
  .handle-info-row {
    display: flex;
    flex-wrap: wrap;
    background-color: #fff;
    padding: 10px;
    border-radius: 4px;
    gap: 20px;
  }
  
  .info-item {
    display: flex;
    align-items: center;
  }
  
  .info-label {
    color: #606266;
    font-size: 13px;
    margin-right: 5px;
  }
  
  /* 附加信息 */
  .additional-info {
    margin-top: 10px;
    font-size: 12px;
    color: #909399;
    display: flex;
    justify-content: flex-end;
  }
  
  .source-tag {
    background-color: #f0f2f5;
    padding: 2px 6px;
    border-radius: 2px;
    margin-right: 5px;
  }
  
  .source-id {
    color: #c0c4cc;
  }
  
  /* 表格容器样式 */
  .alert-unhandled-container,
  .alert-records-container {
    padding: 5px;
  }

  /* 新增告警容器样式 */
  .alert-add-container {
    padding: 10px 15px;
  }

  .add-info-card {
    background-color: #f8f9fa;
    border-radius: 4px;
    padding: 18px 20px 12px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  }

  .add-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 4px;
  }
  
  /* 工具栏样式 */
  .toolbar {
    margin-bottom: 15px;
    display: flex;
    justify-content: space-between;
  }
  
  /* 搜索表单样式 */
  .search-form {
    margin-bottom: 15px;
    border-bottom: 1px solid #eaeaea;
    padding-bottom: 10px;
  }
  
  /* 分页容器样式 */
  .pagination-container {
    margin-top: 15px;
    text-align: right;
  }

  /* 操作按钮样式 */
  .operation-buttons {
    display: flex;
    gap: 6px;
    justify-content: center;
    align-items: center;
  }

  .operation-buttons .el-button {
    margin: 0;
    padding: 5px 8px;
    font-size: 12px;
  }
  
  /* 辅助类 */
  .el-divider {
    margin: 10px 0;
  }

  /* 搜索表单样式优化 */
  .compact-form .el-form-item {
    margin-bottom: 5px;
    margin-right: 5px;
  }
  
  .search-buttons {
    display: flex;
    gap: 5px;
  }
  
  .search-buttons-item {
    margin-right: 0;
  }
  
  /* 表格样式优化 */
  .expanded-row {
    padding: 8px 15px;
    background-color: #fafafa;
  }
  
  .alert-message-expanded {
    color: #606266;
    line-height: 1.5;
    font-size: 13px;
  }
  
  .alert-message-cell {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }

  /* 操作列下拉菜单样式 */
  .el-dropdown {
    width: 100%;
  }

  .el-dropdown .el-button {
    width: 100%;
    text-align: center;
  }

  .el-dropdown-menu__item {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .el-dropdown-menu__item i {
    margin-right: 0;
  }

  /* 搜索表单优化 */
  .search-form .compact-form {
    margin-bottom: 0;
  }
  
  .expand-btn {
    margin-left: 8px;
    color: #409EFF;
  }

  .expand-btn:hover {
    color: #66b1ff;
  }

  .expand-search-row {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px dashed #e0e0e0;
  }

  /* 批量操作栏样式 */
  .batch-action-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    margin-bottom: 8px;
    background: linear-gradient(135deg, #ecf5ff 0%, #f0f9eb 100%);
    border: 1px solid #b3d8ff;
    border-radius: 4px;
  }

  .batch-action-bar .selected-info {
    display: flex;
    align-items: center;
    gap: 4px;
    color: #409EFF;
    font-size: 13px;
  }

  .batch-action-bar .selected-info i {
    font-size: 14px;
  }

  .batch-action-bar .selected-info strong {
    color: #e6a23c;
    font-size: 15px;
    margin: 0 2px;
  }

  /* 告警详情图片显示样式 */
  .alert-detail-images-section {
    margin-top: 15px;
  }

  .alert-detail-images-container {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 10px;
  }

  .alert-detail-image-item {
    position: relative;
    width: 80px;
    height: 80px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .alert-detail-image-item:hover {
    border-color: #409EFF;
    box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
  }

  .alert-detail-image-thumb {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  /* 处理图片显示样式 */
  .handle-images-section {
    margin-top: 15px;
  }

  .handle-images-container {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 10px;
  }

  .handle-image-item {
    position: relative;
    width: 80px;
    height: 80px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .handle-image-item:hover {
    border-color: #409EFF;
    box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
  }

  .handle-image-thumb {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  .handle-image-item:hover .image-overlay {
    opacity: 1;
  }

  .image-overlay i {
    color: white;
    font-size: 20px;
  }

  .image-error {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f5f7fa;
    color: #909399;
    font-size: 12px;
    text-align: center;
  }

  /* 处理图片预览弹窗样式 */
  .handle-image-preview-container {
    display: flex;
    justify-content: center;
    align-items: center;
    max-height: 70vh;
    overflow: hidden;
  }

  .handle-image-preview {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    border-radius: 4px;
  }

  /* 处理记录：截图缩略图（多张，点击可放大） */
  .handle-thumbs {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-wrap: nowrap;
    justify-content: center;
  }

  .handle-thumbs .handle-thumb {
    width: 30px;
    height: 30px;
    border-radius: 3px;
    border: 1px solid #e4e7ed;
    cursor: zoom-in;
    flex: 0 0 auto;
    overflow: hidden;
  }

  .handle-thumbs .thumb-more {
    font-size: 12px;
    color: #909399;
    flex: 0 0 auto;
  }

  .handle-no-image {
    color: #c0c4cc;
  }
</style>